import { useEffect } from 'react';

/**
 * Mounts a single global rAF-throttled `pointermove` listener that writes
 * `--mouse-x` (px) and `--mouse-y` (px, viewport-relative) to the document
 * root, so CSS consumers can drive cursor-aware effects (spotlight, parallax)
 * via `radial-gradient(... var(--mouse-x, -9999px) var(--mouse-y, -9999px))`
 * without triggering any React renders on every pointer move.
 *
 * Gated on `(pointer: fine) and (prefers-reduced-motion: no-preference)` —
 * touch devices and reduced-motion users get no listener and the CSS var
 * fallback resolves off-canvas (`-9999px`), so the spotlight effect is
 * invisible. Pattern mirrors `useScrollVars` (workspace lesson L-190).
 *
 * `pointerleave` on the window resets the position off-canvas so any
 * spotlight fades when the cursor exits the viewport.
 */

let refCount = 0;
let raf = 0;
let lastX = -9999;
let lastY = -9999;
let moveListener: ((e: PointerEvent) => void) | null = null;
let leaveListener: (() => void) | null = null;
let mqListener: (() => void) | null = null;
let mq: MediaQueryList | null = null;

function shouldAttach(): boolean {
  return window.matchMedia('(pointer: fine) and (prefers-reduced-motion: no-preference)').matches;
}

function commit() {
  raf = 0;
  const root = document.documentElement;
  root.style.setProperty('--mouse-x', `${lastX}px`);
  root.style.setProperty('--mouse-y', `${lastY}px`);
}

function attach() {
  if (moveListener) return;
  const onMove = (e: PointerEvent) => {
    lastX = e.clientX;
    lastY = e.clientY;
    if (raf) return;
    raf = requestAnimationFrame(commit);
  };
  const onLeave = () => {
    lastX = -9999;
    lastY = -9999;
    if (raf) return;
    raf = requestAnimationFrame(commit);
  };
  moveListener = onMove;
  leaveListener = onLeave;
  window.addEventListener('pointermove', onMove, { passive: true });
  window.addEventListener('pointerleave', onLeave);
  commit();
}

function detach() {
  if (raf) {
    cancelAnimationFrame(raf);
    raf = 0;
  }
  if (moveListener) {
    window.removeEventListener('pointermove', moveListener);
    moveListener = null;
  }
  if (leaveListener) {
    window.removeEventListener('pointerleave', leaveListener);
    leaveListener = null;
  }
  const root = document.documentElement;
  root.style.removeProperty('--mouse-x');
  root.style.removeProperty('--mouse-y');
}

export function usePointerVars(): void {
  useEffect(() => {
    refCount += 1;

    if (refCount === 1) {
      mq = window.matchMedia('(pointer: fine) and (prefers-reduced-motion: no-preference)');
      if (mq.matches) attach();
      mqListener = () => {
        if (shouldAttach()) attach();
        else detach();
      };
      mq.addEventListener('change', mqListener);
    }

    return () => {
      refCount -= 1;
      if (refCount === 0) {
        if (mq && mqListener) mq.removeEventListener('change', mqListener);
        mq = null;
        mqListener = null;
        detach();
      }
    };
  }, []);
}
